// Wave 20.49ah — Intensity of one session vs the rider's own
// history. Web parity for the mobile IntensityCard. Two states:
//   locked → renders a countdown ("Record N more session(s) to
//            unlock intensity"). Same three-session gate as
//            mobile.
//   unlocked → renders a horizontal min→max bar with a median
//              tick + this session's dot + a percentile chip.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  IconButton,
  Popper,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors } from '../theme';
import { fmtSpeed, type UnitSystem, useUnitSystem } from '../services/useUnitSystem';

// Wave 20.49ap — Section title with an inline info affordance.
// The (i) opens a small popper explaining the metric so the
// rider knows what "Aero intensity" actually measures. Click
// again (or outside) to dismiss.
function InfoTitle({
  title,
  explainer,
}: {
  title: string;
  explainer: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  return (
    <>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          color: colors.brand,
          fontSize: '0.7rem',
          fontWeight: 800,
          letterSpacing: '0.14em',
        }}
      >
        {title}
        <IconButton
          size="small"
          onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}
          sx={{ p: 0.25, ml: 0.25, color: colors.textMuted }}
          aria-label="What is this metric?"
        >
          <InfoOutlinedIcon sx={{ fontSize: '0.95rem' }} />
        </IconButton>
      </Box>
      <Popper
        open={open}
        anchorEl={anchor}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      >
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
          <Box
            sx={{
              maxWidth: 320,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
              boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
            }}
          >
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem', lineHeight: 1.4 }}>
              {explainer}
            </Typography>
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface Intensity {
  unlocked: boolean;
  activityType:
    | 'indoor_aero'
    | 'outdoor_cycling'
    | 'outdoor_running'
    | null;
  sessionsNeeded: number;
  metric: 'aero_wind_mps' | 'power' | 'hr' | 'speed_mps' | null;
  metricLabel: string | null;
  percentile: number | null;
  sessionValue: number | null;
  history: {
    min: number;
    median: number;
    max: number;
    count: number;
  } | null;
}

function fmtValue(
  metric: Intensity['metric'],
  v: number,
  units: 'metric' | 'imperial',
): string {
  if (metric === 'power') return `${Math.round(v)} W`;
  if (metric === 'hr') return `${Math.round(v)} bpm`;
  // Wave 20.49aq — aero_wind_mps + speed_mps respect the
  // rider's unit-system preference so an imperial rider sees
  // mph everywhere, not km/h just here.
  return fmtSpeed(v, units);
}


// Wave 20.70 — the numbers in this explainer were hardcoded metric,
// so an imperial user read mph in the tiles above and km/h in the
// sentence explaining them. 6.94 / 4.17 / 11.11 m/s are 25 / 15 / 40
// km/h, kept as m/s so the formatter owns the conversion.
function intensityExplainer(units: UnitSystem): string {
  const ride = fmtSpeed(6.944, units);
  const headwind = fmtSpeed(4.167, units);
  const effort = fmtSpeed(11.111, units);
  return (
    'Aero intensity is your average speed plus average headwind. It represents ' +
    `the average relative wind you were fighting — a ${ride} ride into a ` +
    `${headwind} headwind reads as a ${effort} effort because aerodynamically ` +
    "it is one. The percentile compares this session against your history of " +
    "the same activity type; when weather data isn't available it falls back " +
    'to power, heart rate, or plain speed.'
  );
}

export default function IntensityCard({ sessionId }: { sessionId: string }) {
  const [data, setData] = useState<Intensity | null>(null);
  const units = useUnitSystem();
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/sessions/${sessionId}/intensity`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j) setData(j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!data) return null;

  if (!data.unlocked) {
    const n = Math.max(1, data.sessionsNeeded);
    // Wave 20.49aj — activity-specific noun so the count is
    // unambiguous. Running intensity + cycling intensity are
    // tracked independently by the backend (same-type filter).
    const noun =
      data.activityType === 'outdoor_running'
        ? n === 1 ? 'run' : 'runs'
        : data.activityType === 'outdoor_cycling'
        ? n === 1 ? 'ride' : 'rides'
        : data.activityType === 'indoor_aero'
        ? n === 1 ? 'aero session' : 'aero sessions'
        : n === 1 ? 'session' : 'sessions';
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.surface,
        }}
      >
        <InfoTitle
          title="AERO INTENSITY"
          explainer={intensityExplainer(units)}
        />
        <Typography sx={{ color: colors.text, fontSize: '1rem', fontWeight: 700, mt: 0.5 }}>
          {`${n} more ${noun} to unlock`}
        </Typography>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem', mt: 0.5 }}>
          Intensity compares each session against your own history for the
          same activity — three {noun} unlocks it. Running and cycling
          intensity are tracked separately.
        </Typography>
      </Box>
    );
  }

  const { percentile, sessionValue, history, metricLabel, metric } = data;
  if (percentile == null || sessionValue == null || !history || !metricLabel) {
    return null;
  }
  const { min, median, max, count } = history;
  const span = Math.max(1e-6, max - min);
  const pctPos = Math.min(100, Math.max(0, ((sessionValue - min) / span) * 100));
  const medPos = Math.min(100, Math.max(0, ((median - min) / span) * 100));
  const heat =
    percentile >= 80 ? colors.brand : percentile >= 40 ? colors.warning : colors.textMuted;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <InfoTitle
          title="AERO INTENSITY"
          explainer={intensityExplainer(units)}
        />
        <Chip
          label={`${percentile}TH PERCENTILE`}
          size="small"
          sx={{
            color: heat,
            border: `1px solid ${heat}`,
            backgroundColor: 'transparent',
            fontWeight: 800,
            fontSize: '0.65rem',
            letterSpacing: '0.08em',
          }}
        />
      </Box>
      <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem', mb: 1.5 }}>
        This session's {metricLabel.toLowerCase()} ({fmtValue(metric, sessionValue, units)}) vs
        your last {count} same-type sessions.
      </Typography>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: colors.border }}>
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            left: `${medPos}%`,
            width: 2,
            height: 12,
            backgroundColor: colors.textMuted,
            transform: 'translateX(-1px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            left: `${pctPos}%`,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: heat,
            border: `2px solid ${heat}`,
            transform: 'translateX(-8px)',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography sx={{ color: colors.textFaint, fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace' }}>
          {fmtValue(metric, min, units)}
        </Typography>
        <Typography sx={{ color: colors.textFaint, fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace' }}>
          {fmtValue(metric, max, units)}
        </Typography>
      </Box>
    </Box>
  );
}
